import { useEffect, useState } from 'react';
import { Select } from '@medeloop/medeloop-components-library';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from 'src/store/toast';
import SaveCodeSetModal from './save-codeset-modal';
import { formatRelevance } from './utils';
import { activeTabsUpdated, cohortStepsUpdated, selectCohortState } from 'src/store/create-cohort';
import { CohortStepType } from 'src/types/cohorts';
import { updateCohortSteps } from 'src/api/create-cohort/fetchers';
import { ConceptCode } from './types';
import { useCodesetManagement } from './hooks/use-codeset-management';
import { CodeSearch } from './code-search';
import SortableCodeTable from './sortable-code-table';

interface CodesTabContentProps {
  table_name: string;
  codeset_name: string;
  codeset_description: string;
  search_term: string;
  items: ConceptCode[];
  initialSavedCodeId?: string;
  tableIndex: number;
  jobId: string;
  onCodesetSaved?: (codesetId: string) => void;
  isFromSavedCodeset?: boolean;
}

const CodesTabContent: React.FC<CodesTabContentProps> = ({
  table_name: initialTableName,
  codeset_name: initialCodesetName,
  codeset_description: initialCodesetDescription,
  search_term: initialSearchTerm,
  items: initialItems,
  initialSavedCodeId,
  tableIndex,
  jobId,
  onCodesetSaved,
  isFromSavedCodeset = false,
}) => {
  const dispatch = useDispatch();
  const cohortState = useSelector(selectCohortState);
  const { allCodesets, datasourceId, refetchCodesets, saveCodeset } = useCodesetManagement();

  // State management
  const [dataItems, setDataItems] = useState<ConceptCode[]>(initialItems);
  const [enabledDataCodesIds, setEnabledDataCodesIds] = useState<string[]>(
    initialItems.filter((x) => x.enabled).map((code) => code.concept_id),
  );
  const [selectedCodesetId, setSelectedCodesetId] = useState<string | undefined>(
    initialSavedCodeId,
  );
  const [isSaveCodeSetModalOpen, setIsSaveCodeSetModalOpen] = useState(false);

  // Metadata state
  const [table_name, setTableName] = useState(initialTableName);
  const [codeset_name, setCodesetName] = useState(initialCodesetName);
  const [codeset_description, setCodesetDescription] = useState(initialCodesetDescription);
  const [search_term, setSearchTerm] = useState(initialSearchTerm);

  // Update active tabs on mount
  useEffect(() => {
    dispatch(
      activeTabsUpdated({
        tabId: tableIndex,
        tabType: isFromSavedCodeset ? 'saved' : 'recommended',
      }),
    );
  }, [tableIndex, isFromSavedCodeset, dispatch]);

  // Handle saved codeset selection
  useEffect(() => {
    if (isFromSavedCodeset && selectedCodesetId) {
      const selectedCodeset = allCodesets?.find((x) => x.id === selectedCodesetId);
      if (selectedCodeset) {
        const newDataItems = selectedCodeset.data.map((code: any) => ({
          concept_id: code.concept_id,
          concept_name: code.concept_name,
          concept_count: code.concept_count,
          relevance: formatRelevance(code.relevance),
          enabled: true,
          id: code.concept_id,
        }));

        setDataItems(newDataItems);
        setEnabledDataCodesIds(selectedCodeset.data.map((code: any) => code.concept_id));
        setTableName(selectedCodeset.tableName);
        setCodesetName(selectedCodeset.codesetName);
        setCodesetDescription(selectedCodeset.codesetDescription);
        setSearchTerm(selectedCodeset.searchTerm);
      }
    }
  }, [selectedCodesetId, allCodesets, isFromSavedCodeset]);

  const sendUpdateCohortSteps = async (targetSavedCodeSetId: string) => {
    const codesSelectionStep = cohortState.steps[CohortStepType.CodesSelection];
    
    if (!codesSelectionStep?.data || codesSelectionStep.data.length === 0 || !codesSelectionStep.data[0]?.payload?.content) {
      return;
    }

    const contentItem = codesSelectionStep.data[0].payload.content[tableIndex];
    
    if (!contentItem) {
      return;
    }

    const clonedTargetStepData =
      typeof contentItem === 'string'
        ? contentItem
        : {
            ...contentItem,
            matches: {
              ...contentItem.matches,
              saved: {
                ...contentItem.matches.saved,
                code_list_id: targetSavedCodeSetId,
              },
            },
          };

    const updatedCohortState = {
      ...cohortState.steps,
      [CohortStepType.CodesSelection]: {
        ...codesSelectionStep,
        data: [
          {
            ...codesSelectionStep.data[0],
            payload: {
              ...codesSelectionStep.data[0].payload,
              content: [
                ...codesSelectionStep.data[0].payload.content.slice(0, tableIndex),
                clonedTargetStepData,
                ...codesSelectionStep.data[0].payload.content.slice(tableIndex + 1),
              ],
            },
          },
        ],
      },
    };

    if (datasourceId && cohortState.cohortId) {
      await updateCohortSteps({
        dataSourceId: datasourceId,
        cohortId: cohortState.cohortId,
        steps: updatedCohortState,
      });
    }

    dispatch(cohortStepsUpdated(updatedCohortState));
  };

  const handleSaveCodeSet = async (name: string, description: string) => {
    try {
      const isUpdate = isFromSavedCodeset && !!selectedCodesetId;
      const result = await saveCodeset(isUpdate, selectedCodesetId, {
        name,
        description,
        table_name,
        search_term,
        items: dataItems,
        enabledIds: enabledDataCodesIds,
      });

      await refetchCodesets();
      setCodesetName(name);
      setCodesetDescription(description);

      const targetId = isUpdate ? selectedCodesetId! : result.id;

      if (!isUpdate && targetId && tableIndex !== undefined && jobId) {
        await sendUpdateCohortSteps(targetId);
      }

      if (onCodesetSaved) {
        onCodesetSaved(targetId);
      }

      if (!isUpdate) {
        setSelectedCodesetId(targetId);
      }

      dispatch(
        showToast({
          type: 'success',
          message: isUpdate ? 'Codeset updated successfully' : 'Codeset created successfully',
        }),
      );
    } catch (error) {
      console.error('Error saving codeset:', error);
      dispatch(
        showToast({
          type: 'dangerous',
          message: 'Error saving codeset',
        }),
      );
    }
  };

  const handleAddCodes = (codes: ConceptCode[]) => {
    setDataItems((prev) => [...prev, ...codes]);
    setEnabledDataCodesIds((prev) => [...prev, ...codes.map((c) => c.concept_id)]);
  };

  const formattedTableName = table_name.split('_').slice(1).join('_');

  return (
    <div>
      {/* Header Section */}
      {isFromSavedCodeset && selectedCodesetId && allCodesets ? (
        <div key={`codeset-${selectedCodesetId}-${allCodesets.length}`}>
          <div>
            <span className="text-white font-bold">
              {formattedTableName}: {search_term}
            </span>
            <div className="w-fit mt-4">
              <Select
                options={(allCodesets || []).map((x) => ({
                  label: `${x.codesetName} (${x.data.length})`,
                  value: x.id,
                }))}
                value={selectedCodesetId}
                onValueChange={(value) => {
                  setDataItems([]);
                  setEnabledDataCodesIds([]);
                  setSelectedCodesetId(value);
                }}
              />
            </div>
          </div>
          <p className="text-on-dark-subdued-content pt-4 text-sm">{codeset_description}</p>
        </div>
      ) : (
        <div>
          <span className="text-white font-bold">
            {formattedTableName}: {search_term}
          </span>
        </div>
      )}

      {/* Search Component */}
      <CodeSearch tableName={table_name} onAddCodes={handleAddCodes} />

      {/* Main Data Table */}
      <SortableCodeTable
        key={`table-${selectedCodesetId || 'empty'}-${dataItems.length}`}
        data={dataItems}
        selectedRows={enabledDataCodesIds}
        onRowSelect={setEnabledDataCodesIds}
        className="rounded-lg max-h-96 overflow-y-auto mb-4"
      />

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <button
          className="border border-gray-600 rounded-full text-white py-2 px-3 text-sm bg-transparent hover:bg-gray-700"
          onClick={() => setIsSaveCodeSetModalOpen(true)}
        >
          Save codeset
        </button>
      </div>

      {/* Save Modal */}
      {isSaveCodeSetModalOpen && (
        <SaveCodeSetModal
          isCodeSetDialogOpen={isSaveCodeSetModalOpen}
          setIsCodeSetDialogOpen={setIsSaveCodeSetModalOpen}
          onSave={handleSaveCodeSet}
          initialCodeSetName={codeset_name}
          initialDescription={codeset_description}
        />
      )}
    </div>
  );
};

export default CodesTabContent;
